import { useCallback, useEffect, useRef } from "react";
import PCMPlayer from "@/features/tutor/components/PCMPlayer";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import { debugLog, debugWarn } from "@/utils/debugLog";
import { decodeAudioChunk } from "@/utils/audioUtils";
import { useAssessmentStore } from "@/features/tutor/store/useAssessmentStore";
import { ASSESSMENT_KEYS } from "@/features/tutor/constants/assessmentTypes";
import { useUIStore } from "@/features/tutor/store/useUIStore";
import {
  useNotepadStore,
  selectIsNotepadOpen,
} from "@/features/notepad/store/useNotepadStore";
import { useAudioStore } from "@/features/tutor/store/useAudioStore";
import { usePlaybackHealthStore } from "@/features/tutor/store/usePlaybackHealthStore";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";
import { isFromOtherTurn } from "@/features/tutor/utils/canvasTurnScope";
import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import {
  applyTutorGain,
  selectTutorAudible,
} from "@/features/tutor/hooks/useTutorAudioGain";
import {
  consumeLessonStartupPerfAnchor,
  consumeSessionStartupPerfAnchor,
} from "@/features/tutor/utils/lessonStartupPerf";

const useAudioPlayer = () => {
  const pcmPlayerRef = useRef(null);
  const isPlayingRef = useRef(false);
  const canvasTurnDonePendingRef = useRef(false);
  const audioTurnIdRef = useRef(null);
  const {
    subscribe,
    emit,
    doneChunkRecievedRef,
    pcmPlayerRef: runtimePcmPlayerRef,
  } = useRuntimeContext();

  const ensurePcmPlayerActive = useCallback(async (reason) => {
    // Don't fight the intentional suspend while the pronunciation modal is open.
    if (useUIStore.getState().isNamePronunciationModalOpen) return;

    const player = pcmPlayerRef.current;
    if (!player || !player.audioCtx) return;

    if (player.audioCtx.state === "closed") {
      player.initAudioContext();
      if (typeof player.bindAudioContextEvent === "function") {
        player.bindAudioContextEvent();
      }
      await player.clear();
    }

    if (player.audioCtx.state !== "running") {
      try {
        await player.audioCtx.resume();
      } catch (err) {
        debugWarn(`[PCMPlayer] resume failed (${reason})`, err);
      }
    }

    usePlaybackHealthStore
      .getState()
      .setAudioSuspended(player.audioCtx.state !== "running");
  }, []);

  useEffect(() => {
    pcmPlayerRef.current = new PCMPlayer({
      inputCodec: "Int16",
      channels: 1,
      sampleRate: 44100,
      flushTime: 0,
      onended: () => {
        const currentTurnId = useTurnStore.getState().getTurnId();
        if (
          doneChunkRecievedRef.current &&
          currentTurnId === audioTurnIdRef.current
        ) {
          emit("AUDIO_PLAYBACK_ENDED");
        }
        if (canvasTurnDonePendingRef.current) {
          canvasTurnDonePendingRef.current = false;
          window.dispatchEvent(new CustomEvent("canvas:audio-playback-ended"));
        }
      },
      onstatechange: (_ctx, _event, state) => {
        // Ignore state changes while the pronunciation modal intentionally
        // suspends the AudioContext — this isn't a playback health issue.
        if (useUIStore.getState().isNamePronunciationModalOpen) return;
        usePlaybackHealthStore
          .getState()
          .setAudioSuspended(state !== "running");
      },
    });

    applyTutorGain(
      () => pcmPlayerRef.current,
      selectTutorAudible(useChatSettingsStore.getState()),
    );

    window.pcmPlayer = pcmPlayerRef.current;
    if (runtimePcmPlayerRef) runtimePcmPlayerRef.current = pcmPlayerRef.current;

    return () => {
      pcmPlayerRef.current.destroy();
      if (runtimePcmPlayerRef) runtimePcmPlayerRef.current = null;
    };
  }, [runtimePcmPlayerRef]);

  useEffect(() => {
    const unsubAudioChunk = subscribe("AUDIO_CHUNK", async (chunk) => {
      const { assessments } = useAssessmentStore.getState();
      const { activeAssessmentModal, isNamePronunciationModalOpen } =
        useUIStore.getState();
      const isNotepadOpen = selectIsNotepadOpen(useNotepadStore.getState());
      if (isNotepadOpen && !useAudioStore.getState().isNudgePending) return;
      if (
        activeAssessmentModal ||
        ASSESSMENT_KEYS.some((k) => assessments[k].isActive)
      )
        return;

      const currentTurnId = useTurnStore.getState().getTurnId();
      if (chunk.turn_id && currentTurnId && chunk.turn_id !== currentTurnId)
        return;

      await ensurePcmPlayerActive("audio_chunk");
      const { chunk_data } = chunk;
      debugLog("audio chunk playing");
      pcmPlayerRef.current.feed(decodeAudioChunk(chunk_data));
      consumeLessonStartupPerfAnchor(
        "lesson_click_to_first_audio_feed",
        {
          turn_id: chunk.turn_id,
        },
        {
          expectedLessonId: useSlideStore.getState().currentLessonId,
        },
      );
      consumeSessionStartupPerfAnchor(
        "session_start_to_first_audio_feed",
        {
          turn_id: chunk.turn_id,
        },
        {
          expectedLessonId: useSlideStore.getState().currentLessonId,
        },
      );
      if (!isPlayingRef.current && !isNamePronunciationModalOpen) {
        isPlayingRef.current = true;
        // Snapshot the turn ID so onended can verify it hasn't changed before
        // emitting AUDIO_PLAYBACK_ENDED — guards against stale onended callbacks
        // firing after pcmPlayer.clear() on interrupt.
        audioTurnIdRef.current = useTurnStore.getState().getTurnId();
        // Anchor wall-clock at the first audio chunk of this AI turn so
        // the WS client can compute audio-elapsed (not wall-clock-since-
        // TURN_CHANGE) for the interrupt timestamp. Backend tokens use
        // audio-elapsed timing; passing wall-clock biases truncation
        // by the pre-audio LLM/STT/TTS latency (~500-1500ms).
        window.__tutorAudioStartWallClockMs = Date.now();
        // Signal that audio is actually about to play (not just TTS started)
        // so dependent UI (e.g. restart prompt buttons) reveals in sync with audio.
        window.dispatchEvent(new CustomEvent("vodemo:audio-start"));
      }
    });

    const unsubTurnChange = subscribe("TURN_CHANGE", () => {
      debugLog("Clearing audio on turn change");
      isPlayingRef.current = false;
      canvasTurnDonePendingRef.current = false;
      audioTurnIdRef.current = null;
      window.__tutorAudioStartWallClockMs = null;
      if (pcmPlayerRef.current) {
        pcmPlayerRef.current.clear();
      }
    });

    const onCanvasTurnDone = (event) => {
      if (isFromOtherTurn(event)) return;
      canvasTurnDonePendingRef.current = true;
    };
    window.addEventListener("canvas:turn-done", onCanvasTurnDone);

    const onCanvasAudioChunk = () => {
      if (!isPlayingRef.current) {
        isPlayingRef.current = true;
        audioTurnIdRef.current = useTurnStore.getState().getTurnId();
      }
    };
    window.addEventListener("canvas:audio-chunk", onCanvasAudioChunk);

    const unsubReset = subscribe("RESET", () => {
      debugLog("Clearing audio on RESET (lesson resume)");
      isPlayingRef.current = false;
      window.__tutorAudioStartWallClockMs = null;
      if (pcmPlayerRef.current) {
        pcmPlayerRef.current.clear();
      }
      usePlaybackHealthStore.getState().reset();
    });

    return () => {
      unsubAudioChunk();
      unsubTurnChange();
      unsubReset();
      window.removeEventListener("canvas:turn-done", onCanvasTurnDone);
      window.removeEventListener("canvas:audio-chunk", onCanvasAudioChunk);
    };
  }, [ensurePcmPlayerActive, subscribe]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        ensurePcmPlayerActive("visibility");
      }
    };

    const handleUserGesture = () => {
      ensurePcmPlayerActive("gesture");
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pageshow", handleVisibility);
    window.addEventListener("touchstart", handleUserGesture);
    window.addEventListener("mousedown", handleUserGesture);
    window.addEventListener("keydown", handleUserGesture);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pageshow", handleVisibility);
      window.removeEventListener("touchstart", handleUserGesture);
      window.removeEventListener("mousedown", handleUserGesture);
      window.removeEventListener("keydown", handleUserGesture);
    };
  }, [ensurePcmPlayerActive]);

  return { ensurePcmPlayerActive };
};

export default useAudioPlayer;
